import {
    AccountUserConsentRequest,
    CircleConsentTypes,
    CircleUpdateUserConsentRequest,
} from '@calm/evexia-web-sdk';
import useAccount from '@rhg/hooks/use-account';
import createAccountApi from '@rhg/libs/api/create-account-api';
import createCircleApi from '@rhg/libs/api/create-circle-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    queryKeys,
    invalidateQueryKey,
    useMutation,
} from '@rhg/libs/react-query';
import { getRhgAppHeader } from '@rhg/utils/browser';
import { useActiveCircle } from '../use-active-circle';
import {
    AccountConsentTypes,
    ConsentTypeContext,
    accountConsentTypeFieldMap,
} from '../use-consent-context/types';

export const VERSION_ERROR = 'Invalid consent version';

interface Props extends AccountUserConsentRequest {
    id: number;
}

export function useUpdateAccountConsent() {
    const { getAccessTokenSilently } = useAuth();
    const { account } = useAccount();
    const rHGApp = getRhgAppHeader();
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({ id, ...payload }: Props) => {
            const api = createAccountApi(getAccessTokenSilently);
            const response = await api.putUserConsentByIdV1({
                userId: account!.userId,
                consentId: id,
                rHGApp,
                accountUserConsentRequest: payload,
            });
            return response;
        },
        onSuccess: async () => {
            // TODO(EVE-10506): callers currently log failures to Datadog only; needs user-facing error UX
            await Promise.all([
                invalidateQueryKey([queryKeys.mostRecentSponsorship]),
                invalidateQueryKey([queryKeys.tosCohortConsent]),
            ]);
        },
    });
    return {
        updateConsent: mutate,
        updateConsentAsync: mutateAsync,
        ...otherProps,
    };
}

export function useUpdateCircleConsent() {
    const { activeCircle } = useActiveCircle();
    const { getAccessTokenSilently } = useAuth();
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async (payload: CircleUpdateUserConsentRequest) => {
            const api = createCircleApi(getAccessTokenSilently);
            const response = await api.updateUserConsents({
                recipientId: activeCircle!.recipient.id,
                circleAuthorizationToken: activeCircle!.authorizationJWT,
                circleUpdateUserConsentRequest: payload,
            });
            return response;
        },
        onSuccess: async () => {
            // TODO(EVE-10506): callers currently log failures to Datadog only; needs user-facing error UX
            await Promise.all([
                invalidateQueryKey([queryKeys.mostRecentSponsorship]),
                invalidateQueryKey([queryKeys.tosCohortConsent]),
            ]);
        },
    });
    return {
        updateConsent: mutate,
        updateConsentAsync: mutateAsync,
        ...otherProps,
    };
}

interface UpdateConsentProps {
    consentContext: ConsentTypeContext;
    accountConsentId: number;
}
export function useUpdateConsent() {
    const { updateConsentAsync: updateCircleConsentAsync } =
        useUpdateCircleConsent();
    const { updateConsentAsync: updateAccountConsentAsync } =
        useUpdateAccountConsent();

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async (payload: UpdateConsentProps) => {
            const { consentContext, accountConsentId } = payload;

            if (!consentContext.requiredVersion) {
                throw new Error(VERSION_ERROR);
            }

            if (
                consentContext.scope === 'circle' &&
                Object.values(CircleConsentTypes).includes(
                    consentContext.consentType as CircleConsentTypes
                )
            ) {
                return updateCircleConsentAsync({
                    consents: [
                        {
                            consentType:
                                consentContext.consentType as CircleConsentTypes,
                            consentVer: consentContext.requiredVersion,
                        },
                    ],
                });
            } else if (
                Object.values(AccountConsentTypes).includes(
                    consentContext.consentType as AccountConsentTypes
                )
            ) {
                return updateAccountConsentAsync({
                    id: accountConsentId,
                    [accountConsentTypeFieldMap[consentContext.consentType]]:
                        consentContext.requiredVersion,
                });
            }
        },
    });
    return {
        updateConsent: mutate,
        updateConsentAsync: mutateAsync,
        ...otherProps,
    };
}
